import { useRef, useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, RefreshCw, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
// BETA: Voice conversation mode hidden for beta release. Will be re-enabled post-beta.
// import { Mic } from "lucide-react";
// import { useInlineVoice } from "../hooks/useInlineVoice";
// import { VoiceModePanel } from "../components/VoiceModePanel";
import { ChatMessage } from "../components/ChatMessage";
import { ChatInput } from "../components/ChatInput";
import { useAccountContext } from "../context/AccountContext";
import { useChatContext } from "../context/ChatContext";

// ---------------------------------------------------------------------------
// Empty state shown before any messages
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6">
      <div
        className={cn(
          "flex h-16 w-16 items-center justify-center rounded-full",
          "bg-brand/10 text-brand"
        )}
      >
        <MessageSquare className="h-8 w-8" />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-text-primary">
        Sales Copilot Chat
      </h2>
      <p className="mt-1 max-w-xs text-center text-sm text-text-secondary">
        Ask about accounts, contacts, order history, or get prep for your next
        visit.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading history spinner
// ---------------------------------------------------------------------------

function HistoryLoader() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6">
      <Loader2 className="h-8 w-8 animate-spin text-brand" />
      <p className="mt-3 text-sm text-text-secondary">
        Loading conversation…
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pull-to-refresh indicator
// ---------------------------------------------------------------------------

interface PullIndicatorProps {
  pulling: boolean;
  pullDistance: number;
}

function PullIndicator({ pulling, pullDistance }: PullIndicatorProps) {
  if (!pulling || pullDistance < 10) return null;
  const progress = Math.min(pullDistance / 80, 1);
  return (
    <div
      className="flex items-center justify-center py-2 text-text-muted"
      style={{ opacity: progress }}
    >
      <RefreshCw
        className={cn("h-5 w-5", progress >= 1 && "animate-spin")}
      />
      <span className="ml-2 text-xs">
        {progress >= 1 ? "Release to start new chat" : "Pull to start new chat"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChatPage
// ---------------------------------------------------------------------------

export function ChatPage() {
  const [searchParams] = useSearchParams();
  const { selectAccount } = useAccountContext();

  // Read account UUID from URL param and sync to context
  const accountUuid = searchParams.get("account") ?? undefined;
  useEffect(() => {
    selectAccount(accountUuid ?? null);
  }, [accountUuid, selectAccount]);

  // Use the shared chat instance from context (persists across tab switches)
  const chat = useChatContext();

  // BETA: Voice conversation mode hidden for beta release. Will be re-enabled post-beta.
  // const voice = useInlineVoice({
  //   sendMessage: chat.sendMessage,
  //   markLastSpoken: chat.markLastSpoken,
  //   isLoading: chat.isLoading,
  // });
  //
  // // Wire up SSE response completion → voice TTS
  // const prevIsLoadingRef = useRef(chat.isLoading);
  // useEffect(() => {
  //   if (prevIsLoadingRef.current && !chat.isLoading && voice.isVoiceModeActive) {
  //     const lastAssistant = [...chat.messages]
  //       .reverse()
  //       .find((m) => m.role === "assistant" && !m.isStreaming);
  //     if (lastAssistant?.content) {
  //       voice.notifyResponseComplete(lastAssistant.content);
  //     }
  //   }
  //   prevIsLoadingRef.current = chat.isLoading;
  // }, [chat.isLoading, chat.messages, voice]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.messages]);

  // Pull-to-refresh state — requires the user to have scrolled down first
  // to prevent accidental triggers when browsing message history.
  const [pullDistance, setPullDistance] = useState(0);
  const [pulling, setPulling] = useState(false);
  const touchStartY = useRef(0);
  const hadScrolledDown = useRef(false);

  // Track whether the user has scrolled down at least 50px before allowing
  // pull-to-refresh. Reset when messages change (new conversation).
  useEffect(() => {
    hadScrolledDown.current = false;
  }, [chat.messages.length]);

  const handleScroll = useCallback(() => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop > 50) {
      hadScrolledDown.current = true;
    }
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (chat.messages.length === 0) return;
      if (!hadScrolledDown.current) return;
      const scrollTop = scrollRef.current?.scrollTop ?? 0;
      if (scrollTop <= 0) {
        touchStartY.current = e.touches[0].clientY;
        setPulling(true);
      }
    },
    [chat.messages.length]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pulling) return;
      const diff = e.touches[0].clientY - touchStartY.current;
      if (diff > 0) {
        setPullDistance(diff);
      }
    },
    [pulling]
  );

  const handleTouchEnd = useCallback(() => {
    if (pulling && pullDistance >= 80) {
      chat.clearConversation();
    }
    setPulling(false);
    setPullDistance(0);
  }, [pulling, pullDistance, chat.clearConversation]);

  const hasMessages = chat.messages.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Messages area — always visible, including during voice mode */}
      <div
        ref={scrollRef}
        className="flex flex-1 flex-col overflow-y-auto"
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {chat.isLoadingHistory ? (
          <HistoryLoader />
        ) : hasMessages ? (
          <>
            <PullIndicator pulling={pulling} pullDistance={pullDistance} />
            <div className="flex-1" />
            <div className="overflow-x-hidden py-2">
              {chat.messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onFeedbackSubmitted={chat.setMessageFeedback}
                />
              ))}
            </div>
            <div ref={messagesEndRef} />
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Error banner */}
      {chat.error && (
        <div className="border-t border-error/20 bg-error/5 px-4 py-2 text-xs text-error">
          {chat.error}
        </div>
      )}

      {/* BETA: Voice conversation mode hidden for beta release. Will be re-enabled post-beta.
      <VoiceModePanel
        voiceState={voice.voiceState}
        isActive={voice.isVoiceModeActive}
        onMicTap={voice.handleMicTap}
        onClose={voice.deactivateVoiceMode}
        transcript={voice.transcript}
        error={voice.error}
        handsFreeMode={voice.handsFreeMode}
        onHandsFreeToggle={voice.setHandsFreeMode}
        analyserNode={voice.analyserNode}
      />
      */}

      {/* Chat input with simple voice-to-text mic and text input */}
      <ChatInput onSend={(text) => chat.sendMessage(text, 'text')} disabled={chat.isLoading} />

      {/* BETA: Voice conversation button hidden for beta release. Will be re-enabled post-beta.
      {!voice.isVoiceModeActive && (
        <div className="flex items-center bg-surface">
          <div className="flex-1">
            <ChatInput onSend={(text) => chat.sendMessage(text, 'text')} disabled={chat.isLoading} />
          </div>
          <button
            type="button"
            onClick={voice.activateVoiceMode}
            aria-label="Start voice conversation"
            data-testid="voice-activate-button"
            className={cn(
              "mr-3 flex h-touch w-touch shrink-0 items-center justify-center rounded-full",
              "bg-brand text-text-inverse active:bg-brand-700",
              "transition-colors"
            )}
          >
            <Mic className="h-5 w-5" />
          </button>
        </div>
      )}
      */}
    </div>
  );
}
